"use client";

/* eslint-disable react/prop-types */

import BFLButton from "@repo/shared-ui/BFLButton";
import BFLModal from "@repo/shared-ui/BFLModal";
import { dlCtaTextConstants } from "@repo/analytics/data-layer.constants";
import { useRouter, useSearchParams } from "next/navigation";
import { FC, memo, useEffect, useMemo, useState } from "react";
import type { IFilters } from "../../../interfaces/StockList.interface";
import type { StockListingUILabels } from "../../../interfaces/StockListingTableAEM.interface";
import {
  createDefaultStockFilters,
  getStockFilterSelectionCount,
  isStockFilterSelected,
  mapStockFilterCategoryToFilterKey,
  normalizeStockFiltersForRequest,
  toggleStockFilterValue,
} from "../utils/stockListingHelpers";
import { useStockListingFilters } from "../context/StockListingFiltersContext";
import type { SortOption } from "./StockSortDropdown";
import {
  mapFilterTabCtaLabel,
  pushStockFilterCtEvent,
  stockFilterCtConstants,
} from "./stockFiltersCt";

const DEFAULT_VISIBLE = 12;

/** Same CT payloads as `StockListingFiltersSidebar` (desktop stocks filter). */
function pushStocksFilterCheckboxClick(categoryName: string, optionValue: string) {
  pushStockFilterCtEvent({
    event: stockFilterCtConstants.checkboxClick,
    element: stockFilterCtConstants.checkboxElement,
    sectionTitle: optionValue,
    filterCategory: categoryName,
    filterSubCategory: optionValue,
  });
}

function pushStocksFilterAlphabetTabClick(letter: string) {
  pushStockFilterCtEvent({
    event: stockFilterCtConstants.tabClick,
    element: stockFilterCtConstants.tabElement,
    sectionTitle: letter,
    filterCategory: letter,
  });
}

function pushStocksFilterIconClick() {
  const filterCta = "Filters1";
  pushStockFilterCtEvent({
    event: stockFilterCtConstants.iconClick,
    element: stockFilterCtConstants.tabElement,
    sectionTitle: filterCta,
    epSubText: filterCta,
    epCta: filterCta,
  });
}

function pushStocksFilterCategoryTabClick(categoryKey: string) {
  const tabCta = mapFilterTabCtaLabel(categoryKey);
  pushStockFilterCtEvent({
    event: stockFilterCtConstants.tabClick,
    element: stockFilterCtConstants.tabElement,
    sectionTitle: tabCta,
    filterCategory: tabCta,
    epSubText: tabCta,
    epCta: tabCta,
  });
}

function pushStocksFilterApplyClick() {
  const applyCta = dlCtaTextConstants.APPLY;
  pushStockFilterCtEvent({
    event: stockFilterCtConstants.buttonClick,
    element: stockFilterCtConstants.checkboxElement,
    sectionTitle: applyCta,
    epSection: applyCta,
    epSubText: applyCta,
    epCta: applyCta,
  });
}

export interface StockFiltersMobileDrawerProps {
  sortValue: string;
  sortOptions: SortOption[];
  showSortSection?: boolean;
  uiLabels?: StockListingUILabels;
}

const StockFiltersMobileDrawer: FC<StockFiltersMobileDrawerProps> = memo(
  ({ sortValue, sortOptions, showSortSection = true, uiLabels = {} }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { filterCategories, isLoading, error, activeFilters, setActiveFilters, clearAll } =
    useStockListingFilters();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("alphabetical");
  const [tempSort, setTempSort] = useState(sortValue);
  const [tempFilters, setTempFilters] = useState<IFilters>(activeFilters);
  const [showAllByKey, setShowAllByKey] = useState<Record<string, boolean>>({});
  const [searchQueryByKey, setSearchQueryByKey] = useState<Record<string, string>>({});

  // Keep temp state in sync when opening
  useEffect(() => {
    if (isOpen) {
      const urlSort = searchParams.get("sort") ?? sortValue;
      setTempSort(urlSort);
      setTempFilters(activeFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const totalSelected = useMemo(() => {
    return getStockFilterSelectionCount(tempFilters);
  }, [tempFilters]);

  const appliedSelectedCount = useMemo(() => {
    return getStockFilterSelectionCount(activeFilters);
  }, [activeFilters]);

  const handleToggleFilter = (
    categoryKey: string,
    categoryName: string,
    optionValue: string,
    optionLabel?: string,
  ) => {
    if (categoryKey === "alphabetical") {
      const prevList = (tempFilters.alphabetic ?? []).map(String);
      const alreadySelected = prevList[0] === optionValue && prevList.length === 1;
      if (!alreadySelected) {
        pushStocksFilterAlphabetTabClick(optionValue);
      }
      setTempFilters((prev) => ({
        ...prev,
        alphabetic: alreadySelected ? [] : [optionValue],
      }));
      return;
    }

    const trackingValue =
      categoryKey === "exchange" ? (optionLabel ?? optionValue) : optionValue;
    pushStocksFilterCheckboxClick(categoryName, trackingValue);

    setTempFilters((prev) =>
      toggleStockFilterValue(prev, categoryKey, optionValue, true),
    );
  };

  const handleClearAll = () => {
    clearAll();
    setTempFilters(createDefaultStockFilters());
  };

  const handleApply = () => {
    pushStocksFilterApplyClick();
    setActiveFilters(normalizeStockFiltersForRequest(tempFilters));
    // Only update the URL when sort actually changes.
    // Removing `router.refresh()` prevents a full page reload; the list will refetch via state changes.
    const currentSort = searchParams.get("sort") ?? sortValue;
    if (currentSort !== tempSort) {
      const params = new URLSearchParams(searchParams.toString());
      params.set("sort", tempSort);
      router.replace(`?${params.toString()}`, { scroll: false });
    }
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpenFilters = () => {
    pushStocksFilterIconClick();
    setIsOpen(true);
  };

  const handleCategoryTabClick = (categoryKey: string) => {
    pushStocksFilterCategoryTabClick(categoryKey);
    setActiveTab(categoryKey);
  };

  const leftNav = filterCategories.length ? filterCategories : [];
  const selectedCategory = leftNav.find((c) => c.key === activeTab) ?? leftNav[0];
  const activeIndex = leftNav.findIndex((c) => c.key === activeTab);

  const renderLeftNavContent = () => {
    if (isLoading) {
      return <div className="p-4 text-[12px] text-gray-600">{uiLabels.loadingText ?? "Loading\u2026"}</div>;
    }
    if (error) {
      return <div className="p-4 text-[12px] text-red-600">{uiLabels.filtersErrorText ?? "Unable to load filters"}</div>;
    }
    return (
      <div className="flex flex-col w-[119px] border-b border-r border-[#d2d2d2]">
        {leftNav.map((cat, idx) => {
          const isActive = activeTab === cat.key;
          const borderBottomClass =
            idx === activeIndex - 1 ? "border-b border-[#d2d2d2]" : "";

          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => handleCategoryTabClick(cat.key)}
              className={`bg-[#f6f6f6] px-[12px] py-2 text-left min-h-[35px] w-[118px] text-[12px] leading-[19.3px] ${
                isActive
                  ? "border-l-2 border-[rgb(255,103,0)] bg-white text-link-primary"
                  : `text-[#000000] border-t border-[#d2d2d2] ${borderBottomClass}`
              }`}
            >
              {cat.name}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="relative shrink-0 border-l border-[rgba(0,0,0,0.12)]">
        <button
          type="button"
          onClick={handleOpenFilters}
          className="box-border flex h-full w-[100px] items-center gap-1 p-4 pl-[10px] text-[12px] md:text-sm text-[rgb(255,103,0)]"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <span>{uiLabels.filtersLabel ?? "Filters"}</span>
          {appliedSelectedCount > 0 && (
            <span className="flex min-w-[15px] h-[15px] max-h-[15px] items-center justify-center rounded-full border border-[#d2d2d2] bg-link-primary px-1 text-[10px] leading-none text-white">
              {appliedSelectedCount}
            </span>
          )}
          <i className="icomoon h-4 w-4 before:content-['\e909']" aria-hidden="true" />
        </button>
      </div>

      {isOpen && (
        <BFLModal
          isOpen={isOpen}
          onClose={handleClose}
          title={uiLabels.filtersLabel ?? "Filters"}
          showHeader={false}
          enableScroll
          modalClassName="items-end justify-center md:hidden"
          contentClassName="w-full rounded-t-lg"
          contentWrapperClassName="px-0"
          footerClassName="hidden"
        >
          <div className="flex flex-col">
            <div className="flex items-center justify-between h-[58px] px-6 py-[20px] border-b border-[#d2d2d2] rounded-t-[4px] bg-gray-200">
              <div className="text-[14px] font-medium text-[#1A1A1A]">
                {`${uiLabels.filtersLabel ?? "Filters"} - ${totalSelected}`}
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
                aria-label={uiLabels.closeAriaLabel ?? "Close"}
              >
                <i
                  className="icomoon text-[10px] text-gray-900 before:content-['\e914']"
                  aria-hidden="true"
                />
              </button>
            </div>

            {/* Sort section */}
            {showSortSection && (
              <div className="px-4 py-4 border-b border-[#d2d2d2]">
                <div className="text-[13px] font-medium text-[#1A1A1A] mb-3">{uiLabels.sortLabel ?? "Sort"}</div>
                <div className="flex flex-col gap-3">
                  {sortOptions.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="sort-option"
                        className="h-4 w-4 min-w-[16px] cursor-pointer appearance-none outline-none bg-white border border-[#d2d2d2] rounded-[2px] checked:bg-[#ff6700] checked:border-[#ff6700]"
                        checked={tempSort === opt.value}
                        onChange={() => setTempSort(opt.value)}
                      />
                      <span className="text-[12px] text-[#1A1A1A]">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Filters section */}
            <div className="flex h-full min-h-[311px] max-h-[311px]">
              <div className="border-r border-[#d2d2d2] min-w-[119px] scrollbar-none">
                {renderLeftNavContent()}
              </div>

              <div className="w-full p-[12px]">
                {!selectedCategory ? (
                  <div className="text-[12px] text-gray-600">{uiLabels.noFiltersText ?? "No filters"}</div>
                ) : (
                  <>
                    {(() => {
                      const resolvedFilterKey = mapStockFilterCategoryToFilterKey(
                        selectedCategory.key,
                      );
                      const selectedList = resolvedFilterKey
                        ? (tempFilters[resolvedFilterKey] ?? [])
                        : [];
                      const showAll = showAllByKey[selectedCategory.key] ?? false;
                      const visible = showAll
                        ? selectedCategory.options
                        : selectedCategory.options.slice(0, DEFAULT_VISIBLE);
                      const hasOverflow = selectedCategory.options.length > DEFAULT_VISIBLE;

                      if (selectedCategory.key === "alphabetical") {
                        return (
                          <div className="flex flex-wrap gap-[18px]">
                            {selectedCategory.options.map((opt) => {
                              const selected = isStockFilterSelected(
                                selectedList,
                                opt.value,
                              );
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() =>
                                    handleToggleFilter(
                                      selectedCategory.key,
                                      selectedCategory.name,
                                      opt.value,
                                      opt.label,
                                    )
                                  }
                                  className={`flex h-[34px] min-w-[34px] w-fit p-2 items-center justify-center rounded border text-[14px] cursor-pointer ${
                                    selected
                                      ? "bg-bfl-blue text-white"
                                      : "border border-[#e5e5e5]"
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        );
                      }

                      if (selectedCategory.key === "sector") {
                        const searchQuery = searchQueryByKey[selectedCategory.key] ?? "";
                        const lowered = searchQuery.toLowerCase();
                        const filteredOptions = selectedCategory.options.filter((opt) =>
                          opt.label.toLowerCase().includes(lowered),
                        );
                        const allVisibleValues = filteredOptions.map((opt) => opt.value);
                        const allSelected =
                          allVisibleValues.length > 0 &&
                          allVisibleValues.every((val) =>
                            isStockFilterSelected(selectedList, val),
                          );

                        const handleSelectAllToggle = () => {
                          setTempFilters((prev) => {
                            const prevSector = (prev.sector ?? []).map(String);
                            if (allSelected) {
                              const remaining: string[] = [];
                              for (const val of prevSector) {
                                if (!allVisibleValues.includes(val)) {
                                  remaining.push(val);
                                }
                              }
                              return { ...prev, sector: remaining };
                            }
                            const merged = Array.from(
                              new Set<string>([...prevSector, ...allVisibleValues]),
                            );
                            return { ...prev, sector: merged };
                          });
                        };

                        return (
                          <div className="flex flex-col gap-3 max-h-[50vh] overflow-y-auto pr-1 h-[-webkit-fill-available]">
                            <div className="relative">
                              <i
                                className="icomoon absolute left-2.5 top-1/2 -translate-y-1/2 text-[14px] text-gray-400 before:content-['\e908']"
                                aria-hidden="true"
                              />
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) =>
                                  setSearchQueryByKey((prev) => ({
                                    ...prev,
                                    [selectedCategory.key]: e.target.value,
                                  }))
                                }
                                placeholder={uiLabels.searchPlaceholder ?? "Search"}
                                className="w-full min-h-[44px] rounded border border-[#d2d2d2] py-2 pl-[30px] pr-3 text-[12px] outline-none"
                              />
                            </div>

                            <label className="flex items-center gap-3">
                              <span className="relative inline-flex h-[18px] w-[18px] min-w-[16px]">
                                <input
                                  type="checkbox"
                                  className="peer h-full w-full cursor-pointer appearance-none outline-none bg-white border border-[#d2d2d2] rounded-[2px] checked:bg-link-primary checked:border-link-primary"
                                  checked={allSelected}
                                  onChange={handleSelectAllToggle}
                                />
                                <svg className="pointer-events-none absolute inset-0 m-auto hidden h-4 w-4 peer-checked:block" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                  <path d="M3 8l3.5 3.5L13 5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                              <span className="text-[12px] text-black leading-[19.2px]">{uiLabels.selectAllLabel ?? "Select All"}</span>
                            </label>

                            {filteredOptions.map((opt) => {
                              const checked = isStockFilterSelected(
                                selectedList,
                                opt.value,
                              );
                              return (
                                <label key={opt.value} className="flex items-center gap-3">
                                  <span className="relative inline-flex h-[18px] w-[18px] min-w-[16px]">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() =>
                                        handleToggleFilter(
                                          selectedCategory.key,
                                          selectedCategory.name,
                                          opt.value,
                                          opt.label,
                                        )
                                      }
                                      className="peer h-full w-full cursor-pointer appearance-none outline-none bg-white border border-[#d2d2d2] rounded-[2px] checked:bg-[#ff6700] checked:border-[#ff6700]"
                                    />
                                    <svg className="pointer-events-none absolute inset-0 m-auto hidden h-4 w-4 peer-checked:block" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                      <path d="M3 8l3.5 3.5L13 5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                  <span className="text-[12px] text-black leading-[19.2px]">{opt.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        );
                      }

                      const allOptionValues = selectedCategory.options.map((opt) => opt.value);
                      const allOptionsSelected =
                        allOptionValues.length > 0 &&
                        allOptionValues.every((val) =>
                          isStockFilterSelected(selectedList, val),
                        );

                      const handleSelectAllToggleDefault = () => {
                        if (!resolvedFilterKey) return;
                        setTempFilters((prev) => {
                          const prevList = (prev[resolvedFilterKey] ?? []).map(String);
                          if (allOptionsSelected) {
                            const remaining: string[] = [];
                            for (const val of prevList) {
                              if (!allOptionValues.includes(val)) remaining.push(val);
                            }
                            return { ...prev, [resolvedFilterKey]: remaining };
                          }
                          const merged = Array.from(
                            new Set<string>([...prevList, ...allOptionValues]),
                          );
                          return { ...prev, [resolvedFilterKey]: merged };
                        });
                      };

                      return (
                        <>
                          <div className="flex flex-col gap-3">
                            <label className="flex items-center gap-3">
                              <span className="relative inline-flex h-[18px] w-[18px] min-w-[16px]">
                                <input
                                  type="checkbox"
                                  className="peer h-full w-full cursor-pointer appearance-none outline-none bg-white border border-[#d2d2d2] rounded-[2px] checked:bg-[#ff6700] checked:border-[#ff6700]"
                                  checked={allOptionsSelected}
                                  onChange={handleSelectAllToggleDefault}
                                />
                                <svg className="pointer-events-none absolute inset-0 m-auto hidden h-4 w-4 peer-checked:block" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                  <path d="M3 8l3.5 3.5L13 5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                              <span className="text-[12px] text-black leading-[19.2px]">{uiLabels.selectAllLabel ?? "Select All"}</span>
                            </label>
                            {visible.map((opt) => {
                              const checked = isStockFilterSelected(
                                selectedList,
                                opt.value,
                              );
                              return (
                                <label key={opt.value} className="flex items-center gap-3 mt-[6px]">
                                  <span className="relative inline-flex h-[18px] w-[18px] min-w-[16px]">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() =>
                                        handleToggleFilter(
                                          selectedCategory.key,
                                          selectedCategory.name,
                                          opt.value,
                                          opt.label,
                                        )
                                      }
                                      className="peer h-full w-full cursor-pointer appearance-none outline-none bg-white border border-[#d2d2d2] rounded-[2px] checked:bg-[#ff6700] checked:border-[#ff6700]"
                                    />
                                    <svg className="pointer-events-none absolute inset-0 m-auto hidden h-4 w-4 peer-checked:block" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                      <path d="M3 8l3.5 3.5L13 5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                  <span className="text-[12px] text-black leading-[19.2px]">{opt.label}</span>
                                </label>
                              );
                            })}
                          </div>
                          {hasOverflow && (
                            <button
                              type="button"
                              onClick={() =>
                                setShowAllByKey((prev) => ({
                                  ...prev,
                                  [selectedCategory.key]: !prev[selectedCategory.key],
                                }))
                              }
                              className="mt-4 text-[12px] font-medium text-[rgb(255,103,0)]"
                            >
                              {showAll ? (uiLabels.showLessText ?? "Show less") : (uiLabels.showMoreText ?? "Show more")}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 px-[10px] py-4 border-t border-[#d2d2d2] bg-white z-2">
              <button
                type="button"
                onClick={handleClearAll}
                className="text-[14px] font-medium text-[rgb(255,103,0)]"
              >
                {uiLabels.clearFiltersText ?? "Clear filters"}
              </button>
              <div className="flex-1" />
              <div className="w-[45%]">
                <BFLButton
                  type="button"
                  onClick={handleApply}
                  className="w-full rounded-[20px] bg-[rgb(255,103,0)] px-[25px] py-[10px] text-[14px] font-medium text-white hover:opacity-90"
                >
                  {uiLabels.applyText ?? "APPLY"}
                </BFLButton>
              </div>
            </div>
          </div>
        </BFLModal>
      )}
    </>
  );
  },
);

StockFiltersMobileDrawer.displayName = "StockFiltersMobileDrawer";

export default StockFiltersMobileDrawer;
